import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	customActionUpdater,
	getAccumulatedActionTypeGuard,
	getPrimaryAccumulatedValue,
	isAccumulatedAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import {
	isFollowUpMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAnswerPart,
	isStreamError,
	isStreamReasoningPart,
	isStreamResponse,
	isStreamTodoSnapshot,
	type TodoItem,
	isStreamTaskStatus,
	isStreamTrace,
	type ParsedSSEMessage,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { deriveFollowUpSendAgentIdentity } from '@atlassian/conversation-assistant-store/controllers/chat/derive-follow-up-send-agent-identity';
import {
	type ChatStorePrivateState,
	type Conversation,
	isStoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { RovoChatStreamTimeoutError } from '@atlassian/conversation-assistant-utils/RovoChatStreamTimeoutError';

import { randomIntFromInterval } from '../../common/utils/id-generator';

import { addActionPart } from './addActionPart';
import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createStreamMarkdownMultiTagProcessor } from './createStreamMarkdownMultiTagProcessor';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { StreamGeneratorError } from './error-classes/StreamGeneratorError';
import { findLastAssistantAction } from './findLastAssistantAction';
import { getAssociatedHumanMessageId } from './getAssociatedHumanMessageId';
import { getLastAgentMessageID } from './getLastAgentMessageID';
import { processResearchStreamMessage } from './processResearchStreamMessage';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { type ReasoningChunkBuffer } from './reasoning-chunk-buffer';
import { replaceAnswerData } from './replaceAnswerData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ChatStoreContainerProps, ResearchStreamState } from './types';
import { updateHumanMessageId } from './updateHumanMessageId';

interface ReconnectStreamMessageParams {
	generator: AsyncGenerator<ParsedSSEMessage<StreamMessage>>;
	messageId: string;
	answerIndex: number;
	conversationId: string;
	traceId: string | undefined;
	isStagingArea: boolean;
	isDeepResearch?: boolean;
	getState: () => ChatStorePrivateState;
	setMessages: ChatStoreContainerProps['setMessages'];
	getMessages: ChatStoreContainerProps['getMessages'];
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	processMessageStreamAction?: ChatStoreContainerProps['processMessageStreamAction'];
	processMessageStreamComplete?: ChatStoreContainerProps['processMessageStreamComplete'];
	processMessageStreamActionClassification?: ChatStoreContainerProps['processMessageStreamActionClassification'];
	onHumanMessageIdReplaced?: ChatStoreContainerProps['onHumanMessageIdReplaced'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	onStreamEvent: (
		event: 'error' | 'chunk',
		streamMessage?: StreamMessage,
		eventId?: string,
	) => void;

	/** True while replaying historical events. Wired from withReplayFlush. */
	getIsReplaying?: () => boolean;

	/** When provided, REASONING_PART replay chunks are buffered here instead of dispatched per event. */
	reasoningChunkBuffer?: ReasoningChunkBuffer;

	/** Called after the generator exhausts (success path). Use to drain caller-side buffers. */
	onStreamEnd?: () => void;

	/** Called immediately when a FINAL_RESPONSE event is received. Use to suppress timeout. */
	onFinalResponse?: () => void;

	onTaskStatusDraft?: (args: {
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
	}) => void;

	onTaskStatusCompleted?: (args: {
		title: string;
		updatedAt?: string;
		completionMessageId?: string;
	}) => void;

	/** Called when a TODO_SNAPSHOT event is received. Mirror of send-message-action's hook. */
	onTodoSnapshot?: (args: { todos: TodoItem[] }) => void;
}

export const processReconnectStreamMessage = async ({
	generator,
	messageId,
	conversationId,
	isDeepResearch,
	isStagingArea,
	answerIndex,
	traceId,
	getMessages,
	setMessages,
	processStreamResearch,
	getState,
	updateCurrentConversation,
	sendAnalyticsEvent,
	onStreamEvent,
	getIsReplaying,
	reasoningChunkBuffer,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	onStreamEnd,
	onFinalResponse,
	onTaskStatusDraft,
	onTaskStatusCompleted,
	onTodoSnapshot,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamActionClassification,
	onHumanMessageIdReplaced,
}: ReconnectStreamMessageParams): Promise<{
	hasReceivedFinalResponse: boolean;
}> => {
	// Gate the details-tag preprocessor to 3P-agent conversations only.
	const shouldPreprocessDetails = shouldPreprocessDetailsForConversation(
		getState(),
		conversationId,
	);
	const processStreamMarkdownMultiTag = createStreamMarkdownMultiTagProcessor({
		shouldPreprocessDetails,
	});
	let latestStreamMessage: StreamMessage | undefined;
	let hasReceivedFinalResponse = false;

	// Research store state shared across stream messages
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	let requestId: string | undefined;

	// Tracks whether we've already seeded `previousAccumulatedData` onto the
	// first live (non-replay) AIFC create action chunk after a reconnect. Gated
	// behind aifc_fix_reconnect_stream_overlap_accumulate. When the overlap-fix
	// gate suppresses replayed chunks, the editor never receives the accumulated
	// pre-reconnect content; we hand it over once via previousAccumulatedData so
	// the editor can seed its document before appending live deltas.
	let hasSeededPreviousAccumulatedData = false;

	try {
		for await (const streamObj of generator!) {
			const streamMessage = streamObj.data;
			latestStreamMessage = streamMessage;
			onStreamEvent('chunk', streamMessage, streamObj.id);

			// TRACE events are not handled in reconnect; drop early to skip
			// the remaining type-checks below.
			if (isStreamTrace(streamMessage)) {
				continue;
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!requestId && 'metadata' in streamMessage) {
				requestId = streamMessage.metadata?.request_id ?? undefined;
			}

			// Buffer REASONING_PART during replay; bufferEvent returns false for
			// orphan chunks (no stepId) so they fall through.
			if (
				reasoningChunkBuffer &&
				getIsReplaying?.() &&
				isStreamReasoningPart(streamMessage) &&
				reasoningChunkBuffer.bufferEvent(streamMessage)
			) {
				continue;
			}

			/**
			 * Research store processing — handles think-deeper traces, implicit round
			 * detection, and research plan updates for all message types.
			 */
			processResearchStreamMessage({
				streamMessage,
				tmpAssistantMessageId: messageId,
				conversationId,
				deepResearchEnabled: isDeepResearch ?? false,
				getMessages,
				setMessages,
				processStreamResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				researchState,
			});

			/**
			 * Task status processing
			 */
			if (isStreamTaskStatus(streamMessage)) {
				const taskMessage = streamMessage.message;
				if (taskMessage.status === 'DRAFT') {
					onTaskStatusDraft?.({
						taskId: taskMessage.task_id,
						title: taskMessage.title,
						updatedAt: taskMessage.updated_at,
						messageId,
					});
				} else if (taskMessage.status === 'COMPLETED') {
					onTaskStatusCompleted?.({
						title: taskMessage.title,
						updatedAt: taskMessage.updated_at,
						completionMessageId: messageId,
					});
				}
			}

			/**
			 *  snapshot processing
			 */
			if (isStreamTodoSnapshot(streamMessage)) {
				onTodoSnapshot?.({ todos: streamMessage.message.todos });
			}

			/**
			 *  ANSWER_PART  processing
			 */
			if (isStreamAnswerPart(streamMessage)) {
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				let processedStreamMessage = streamMessage;
				let hasMarkdownData = false;

				const result = processStreamMarkdownMultiTag(processedStreamMessage);
				processedStreamMessage = result.processedStreamMessage;
				if (Object.keys(result.markdownData).length > 0) {
					hasMarkdownData = true;
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownDataMap({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: processedStreamMessage,
							markdownData: result.markdownData,
							useIndex: answerIndex,
						}),
					);
				}
				if (!hasMarkdownData) {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: processedStreamMessage,
							useIndex: answerIndex,
						}),
					);
				}
			}

			if (
				(isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) &&
				fg('aifc_create_enabled')
			) {
				const updatedMessages = addActionPart({
					chat: getMessages(conversationId),
					messageId,
					actionPart: streamMessage,
					useIndex: answerIndex,
					// Gated behind aifc_fix_reconnect_stream_overlap_accumulate: mirror the
					// live processor so replayed AIFC create chunks accumulate their primary
					// field (e.g. adf) in the store instead of overwriting with the latest
					// chunk. This is what makes the accumulated content available to seed
					// previousAccumulatedData below.
					...(fg('aifc_fix_reconnect_stream_overlap_accumulate')
						? { actionUpdaterOverride: customActionUpdater }
						: {}),
				});

				if (isStreamActionPrepare(streamMessage)) {
					const actionMessage = streamMessage.message;
					setMessages(conversationId, [
						...updatedMessages,
						{ type: 'ACTION_PREPARE', action: actionMessage },
					]);
				} else {
					setMessages(conversationId, updatedMessages);
				}

				// Gated behind aifc_fix_reconnect_stream_overlap so rollback is safe.
				if (fg('aifc_fix_reconnect_stream_overlap')) {
					if (!getIsReplaying?.()) {
						// Gated behind aifc_fix_reconnect_stream_overlap_accumulate: on the
						// first live chunk after replay, hand the accumulated pre-reconnect
						// content to the editor via previousAccumulatedData. The overlap-fix
						// gate suppresses replayed chunks from the editor, so without this the
						// editor has no way to reconstruct what streamed before the reconnect.
						if (
							!hasSeededPreviousAccumulatedData &&
							isStreamActionPart(streamMessage) &&
							fg('aifc_fix_reconnect_stream_overlap_accumulate')
						) {
							const actionKey = streamMessage.message.key;
							if (isAccumulatedAction(actionKey)) {
								const lastAction = findLastAssistantAction(
									getMessages(conversationId),
									(action) => action.key === actionKey,
								);
								const isAccumulatedActionType = getAccumulatedActionTypeGuard(actionKey);
								if (lastAction && isAccumulatedActionType(streamMessage.message.data)) {
									const primaryAccumulatedData = getPrimaryAccumulatedValue(lastAction);
									if (primaryAccumulatedData?.value) {
										streamMessage.message.data.previousAccumulatedData =
											primaryAccumulatedData.value;
										hasSeededPreviousAccumulatedData = true;
									}
								}
							}
						}
						processMessageStreamAction?.(messageId, streamMessage, traceId, requestId, undefined, {
							singleInstrumentationId: messageId,
						});
					} else {
						sendAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'suppressed',
								actionSubject: 'streamActionPart',
								actionSubjectId: 'reconnectReplay',
								source: 'reconnectToInProgressStreamProvider',
								attributes: {
									messageId,
									conversationId,
									reason: 'replay',
									key: isStreamActionPart(streamMessage) ? streamMessage.message?.key : 'prepare',
								},
							},
						});
					}
				} else {
					processMessageStreamAction?.(messageId, streamMessage, traceId, requestId, undefined, {
						singleInstrumentationId: messageId,
					});
				}
			}

			if (isStreamActionComplete(streamMessage) && fg('aifc_create_enabled')) {
				const actionMessage = streamMessage.message;
				setMessages(conversationId, [
					...getMessages(conversationId),
					{ type: 'ACTION_COMPLETE', action: actionMessage },
				]);
				// Gated behind aifc_fix_reconnect_stream_overlap so rollback is safe.
				if (fg('aifc_fix_reconnect_stream_overlap')) {
					if (!getIsReplaying?.()) {
						processMessageStreamComplete?.(messageId, streamMessage, traceId, requestId, {
							singleInstrumentationId: messageId,
						});
					} else {
						sendAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'suppressed',
								actionSubject: 'streamActionComplete',
								actionSubjectId: 'reconnectReplay',
								source: 'reconnectToInProgressStreamProvider',
								attributes: {
									messageId,
									conversationId,
									reason: 'replay',
									key: actionMessage?.key,
								},
							},
						});
					}
				} else {
					processMessageStreamComplete?.(messageId, streamMessage, traceId, requestId, {
						singleInstrumentationId: messageId,
					});
				}
			}

			if (isStreamActionClassification(streamMessage) && fg('aifc_create_enabled')) {
				if (fg('platform_editor_ai_promptversion_in_analytics')) {
					// Only call during live phase. If ACTION_CLASSIFICATION arrives during
					// replay it means the editor already received it before disconnect.
					if (!getIsReplaying?.()) {
						processMessageStreamActionClassification?.(
							messageId,
							streamMessage,
							traceId,
							requestId,
							{ singleInstrumentationId: messageId },
						);
					}
				}
			}

			/**
			 * FINAL_RESPONSE processing
			 */
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				onFinalResponse?.();
				const humanMessageId = streamMessage?.message?.human_message?.id.toString();
				const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
					streamMessage,
					messageId,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails,
				});

				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: messageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
					}),
				);

				// Update the StoreHuman entry's ID from the temporary client-generated ID
				// to the real server-assigned ID. After a page reload this is a no-op because
				// the store already contains the real ID.
				if (humanMessageId) {
					const currentHumanMessageId = getAssociatedHumanMessageId(
						getMessages(conversationId),
						messageId,
					);
					if (currentHumanMessageId && currentHumanMessageId !== humanMessageId) {
						setMessages(
							conversationId,
							updateHumanMessageId({
								chat: getMessages(conversationId),
								oldId: currentHumanMessageId,
								newId: humanMessageId,
								onMessageIdChanged: (oldId, newId) =>
									onHumanMessageIdReplaced?.(conversationId, oldId, newId),
							}),
						);
					}
				}
			}

			/**
			 * Follow up processing
			 */
			if (isFollowUpMessage(streamMessage)) {
				const messages = getMessages(conversationId);
				const originatingAssistant = [...messages].reverse().find(isStoreAssistant);
				const originatingAgentIdentity =
					isExperimentEnabled('rovo_chat_mention_agents') && originatingAssistant
						? deriveFollowUpSendAgentIdentity(originatingAssistant)
						: undefined;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: streamMessage,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
						...(originatingAgentIdentity ? { originatingAgentIdentity } : {}),
					},
				]);
			}

			/**
			 * Action error processing
			 */
			if (isStreamActionError(streamMessage)) {
				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea,
							},
						},
						traceId,
					},
				);
				throw actionError;
			}

			if (isStreamError(streamMessage)) {
				onStreamEvent('error', streamMessage, streamObj.id);
				const { message, metadata, millisOffset, type } = streamMessage;

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId,
				});
			}
		}
		// Drain any remaining buffered chunks and clear the periodic timer.
		//cancelReasoningFlush();
		onStreamEnd?.();
	} catch (error: unknown) {
		if (error instanceof StreamErrorException) {
			throw error;
		}

		// Let timeout errors propagate unwrapped so the outer catch can handle them correctly.
		if (error instanceof RovoChatStreamTimeoutError) {
			throw error;
		}

		throw new StreamGeneratorError(
			(error instanceof Error ? error.message : undefined) ?? 'Stream generator error',
			{
				traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			error,
		);
	}

	return { hasReceivedFinalResponse };
};
